import { useEffect, useRef, useState } from 'react';
import { RankedAvatar } from './RankedAvatar';
import { GuildMark } from './GuildMark';
import { useLookups, useStore } from '../lib/store';

/** How long the wheel runs, whatever the player count. */
const ROLL_MS = 4500;

/**
 * Rolls for who goes first.
 *
 * The winner is decided before the animation starts, not by where it happens to
 * stop — an animation that decides the outcome is one that can be argued with,
 * and this is the one moment of the evening where nobody should be able to.
 *
 * The cycling slows as it goes, which is the whole trick: it reads as a wheel
 * losing momentum rather than a list flashing at random.
 */
export function StartingPlayer({
  seats,
  chosen,
  draft = false,
  onBack,
  onDone,
}: {
  seats: { pilotId: string; deckId: string }[];
  chosen: number;
  /**
   * A draft rolls before it deals, so this screen leads somewhere else.
   *
   * The roll means the same thing either way — it is what decides the order —
   * but in a draft the next screen is the pool, not the table, and a button
   * that names the wrong destination is worse than one that names none.
   */
  draft?: boolean;
  /** Back to setup. The roll is re-drawn, which is the honest thing to do. */
  onBack: () => void;
  onDone: () => void;
}) {
  const { state } = useStore();
  const { pilot } = useLookups();
  const [highlight, setHighlight] = useState(0);
  const [settled, setSettled] = useState(false);
  const timer = useRef<number>(0);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setHighlight(chosen);
      setSettled(true);
      return;
    }

    // Six whole laps, then stop on the chosen seat. More laps rather than
    // slower ones: a longer roll should feel like it is spinning for longer,
    // not like each name is being read out.
    const steps = seats.length * 6 + (chosen % seats.length);

    /**
     * Delays are derived from a fixed total rather than a fixed per-step time,
     * so a four-player roll does not take twice as long as a two-player one.
     * The weights ease out, which is what makes it read as a wheel losing
     * momentum instead of a list flashing.
     */
    const weights = Array.from({ length: steps }, (_, i) => {
      const progress = (i + 1) / steps;
      // Cubed rather than squared: it holds a quick pace for longer and then
      // drops off hard at the end, so the last two or three names are the
      // suspenseful part instead of the whole thing being slow.
      return 0.2 + progress * progress * progress * 4;
    });
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    const delays = weights.map((w) => (w / totalWeight) * ROLL_MS);

    let step = 0;
    const tick = () => {
      step += 1;
      setHighlight(step % seats.length);
      if (step >= steps) {
        setSettled(true);
        return;
      }
      timer.current = window.setTimeout(tick, delays[step]!);
    };

    timer.current = window.setTimeout(tick, 180);
    return () => window.clearTimeout(timer.current);
  }, [seats.length, chosen]);

  const winner = pilot(seats[chosen]!.pilotId);

  return (
    <div className="roll">
      {/* Same header as setup, so the roll reads as the next step of one flow
          rather than a screen that arrived from somewhere else. */}
      <div className="contest-head">
        <button className="back-chevron" onClick={onBack} aria-label="Back">
          ‹
        </button>
        <GuildMark name={state.settings.guildName} src={state.settings.guildIcon} size={72} />
        <div>
          <h2 className="contest-title">{state.settings.guildName}</h2>
          <p className="contest-sub">{draft ? 'Draft contest' : 'Guild contest'}</p>
        </div>
      </div>

      {/* Only while it is still running. Once it lands, the result banner below
          says who — saying it twice, once above the list and once under it, was
          two labels competing to be the answer. */}
      {!settled && (
        <p className="seat-label" style={{ textAlign: 'center' }}>
          Rolling for turn order
        </p>
      )}

      <div className="roll-list">
        {seats.map((seat, i) => {
          const p = state.pilots.find((x) => x.id === seat.pilotId);
          const lit = settled ? i === chosen : i === highlight;
          return (
            <div key={seat.pilotId} className={`roll-seat${lit ? ' lit' : ''}`}>
              <RankedAvatar pilot={p} size={58} />
              <span className="roll-name">{p?.name}</span>
            </div>
          );
        })}
      </div>

      {settled && (
        <div className="roll-result">
          {/*
            "Goes first" rather than "on the play". The jargon is correct — the
            first player is on the play and skips their first draw — but it only
            reads as an answer if you already know the term, and the whole point
            of this banner is to answer a question.
          */}
          <div className="reveal">
            <div className="kicker">Goes first</div>
            <div className="name">{winner?.name}</div>
          </div>
          {/* Setup says Begin; this is the next step, not the same one again. */}
          <button className="btn primary block" onClick={onDone}>
            {draft ? 'Start Draft' : 'To The Table'}
          </button>
        </div>
      )}
    </div>
  );
}
